import { useState, useEffect, useCallback } from 'react';

export interface LiveMetrics {
  // Ticker
  btcPrice: number;
  btcChange24h: number;
  btcVol24h: number;
  ethPrice: number;
  ethChange24h: number;
  spx: number | null;
  spxChange: number | null;
  gold: number | null;
  goldChange: number | null;
  dxy: number | null;
  dxyChange: number | null;
  tenYield: number | null;
  tenYieldChange: number | null;
  // On-chain
  ma50: number;
  ma200: number;
  ma200w: number;
  rsi14: number;
  fearGreed: number;
  fearGreedLabel: string;
  btcDominance: number | null; // % of total crypto market cap
  daysToHalving: number | null; // days until next halving (computed)
  // Placeholders (no free API):
  mvrv: number | null;
  // Removed: hashrate (replaced by btcDominance), lthSupply (replaced by daysToHalving)
  fundingRate: number | null;
  etfFlows7d: number | null;
  powerLawFairValue: number | null;
  // M2 Money Supply
  m2Yoy: number | null;
  // Meta
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

const CACHE_KEY = 'utxomacro_live_metrics_v2';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: Omit<LiveMetrics, 'loading' | 'error' | 'lastUpdated'>;
  timestamp: number;
}

function loadCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    return entry;
  } catch {
    return null;
  }
}

function saveCache(data: Omit<LiveMetrics, 'loading' | 'error' | 'lastUpdated'>): void {
  try {
    const entry: CacheEntry = { data, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage may be unavailable — ignore
  }
}

// ── RSI (14D) using Wilder's smoothing ──────────────────────────────────────
function computeRSI14(closes: number[]): number {
  if (closes.length < 15) return 50;
  const deltas = closes.slice(1).map((c, i) => c - closes[i]);
  const gains = deltas.map(d => (d > 0 ? d : 0));
  const losses = deltas.map(d => (d < 0 ? -d : 0));

  // Seed: simple avg of first 14 periods
  let avgGain = gains.slice(0, 14).reduce((a, b) => a + b, 0) / 14;
  let avgLoss = losses.slice(0, 14).reduce((a, b) => a + b, 0) / 14;

  // Wilder's smoothing for remaining periods
  for (let i = 14; i < gains.length; i++) {
    avgGain = (avgGain * 13 + gains[i]) / 14;
    avgLoss = (avgLoss * 13 + losses[i]) / 14;
  }

  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
}

// ── Cloudflare Worker proxy (bypasses Yahoo Finance CORS) ───────────────────
const PROXY_URL = 'https://utxomacro-proxy.utxomacro.workers.dev';

interface ProxyQuote {
  symbol?: string;
  price?: number;
  change?: number;
  error?: string;
  // BTC_MA200W
  ma200w?: number;
  weeksUsed?: number;
  // FUNDING_RATE
  fundingRate?: number;
  // MANUAL
  lthSupplyPct?: number | null;
  etfFlows7d?: number | null;
  updatedAt?: string | null;
  // BTC_DOMINANCE
  btcDominancePct?: number;
  cached?: boolean;
  // M2_YOY
  m2Yoy?: number;
  latestDate?: string;
}

async function fetchAllYahooViaProxy(): Promise<Record<string, ProxyQuote>> {
  try {
    const res = await fetch(`${PROXY_URL}/quotes`);
    if (!res.ok) return {};
    return await res.json();
  } catch {
    return {};
  }
}

// ── Main fetch ───────────────────────────────────────────────────────────────
async function fetchAllMetrics(): Promise<Omit<LiveMetrics, 'loading' | 'error' | 'lastUpdated'>> {
  const [
    cgSimple,
    cgDaily,
    fng,
    proxyQuotes,
  ] = await Promise.allSettled([
    fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum&vs_currencies=usd&include_24hr_change=true&include_24hr_vol=true'
    ).then(r => r.json()),
    fetch(
      'https://api.coingecko.com/api/v3/coins/bitcoin/market_chart?vs_currency=usd&days=200&interval=daily'
    ).then(r => r.json()),
    fetch('https://api.alternative.me/fng/?limit=1').then(r => r.json()),
    fetchAllYahooViaProxy(),
  ]);

  // ── CoinGecko simple price ──
  let btcPrice = 0;
  let btcChange24h = 0;
  let btcVol24h = 0;
  let ethPrice = 0;
  let ethChange24h = 0;
  if (cgSimple.status === 'fulfilled') {
    const d = cgSimple.value;
    btcPrice = d?.bitcoin?.usd ?? 0;
    btcChange24h = d?.bitcoin?.usd_24h_change ?? 0;
    btcVol24h = d?.bitcoin?.usd_24h_vol ?? 0;
    ethPrice = d?.ethereum?.usd ?? 0;
    ethChange24h = d?.ethereum?.usd_24h_change ?? 0;
  }

  // ── Daily chart → MA50, MA200, RSI ──
  let ma50 = 0;
  let ma200 = 0;
  let rsi14 = 50;
  if (cgDaily.status === 'fulfilled') {
    const prices: number[] = (cgDaily.value?.prices ?? []).map(
      (p: [number, number]) => p[1]
    );
    if (prices.length >= 200) {
      ma200 = prices.slice(-200).reduce((a: number, b: number) => a + b, 0) / 200;
    }
    if (prices.length >= 50) {
      ma50 = prices.slice(-50).reduce((a: number, b: number) => a + b, 0) / 50;
    }
    if (prices.length >= 15) {
      rsi14 = computeRSI14(prices);
    }
  }

  // ── 200W MA + Funding Rate via proxy ──
  let ma200w = 0;
  let fundingRate: number | null = null;
  if (proxyQuotes.status === 'fulfilled') {
    ma200w = proxyQuotes.value?.['BTC_MA200W']?.ma200w ?? 0;
    const fr = proxyQuotes.value?.['FUNDING_RATE']?.fundingRate;
    fundingRate = fr !== undefined ? fr : null;
  }

  // ── BTC Dominance + M2 YoY — via Cloudflare Worker proxy (CORS-safe) ──
  let btcDominance: number | null = null;
  let m2Yoy: number | null = null;
  if (proxyQuotes.status === 'fulfilled') {
    const dom = proxyQuotes.value?.['BTC_DOMINANCE']?.btcDominancePct;
    btcDominance = dom !== undefined ? dom : null;
    const m2 = proxyQuotes.value?.['M2_YOY']?.m2Yoy;
    m2Yoy = m2 !== undefined ? m2 : null;
  }

  // ── Next Halving Countdown (computed — no API) ──
  // Block 1,050,000 — est. ~April 20, 2028 based on 10-min average block time from block 840,000 (Apr 19, 2024)
  const nextHalvingDate = new Date('2028-04-20T00:00:00Z');
  const daysToHalving = Math.ceil((nextHalvingDate.getTime() - Date.now()) / 86_400_000);

  // ── MVRV approximation: price / 200D MA (realized cap proxy) ──
  // True MVRV requires UTXO data (paid API). This ratio is directionally accurate.
  const mvrv = ma200 > 0 && btcPrice > 0 ? parseFloat((btcPrice / ma200).toFixed(2)) : null;

  // ── Power Law Fair Value (Giovanni Santostasi model) ──
  // Price = 10^(5.84 * log10(daysSinceGenesis) - 17.01)
  const genesisMs = new Date('2009-01-03').getTime();
  const daysSinceGenesis = (Date.now() - genesisMs) / 86400000;
  const powerLawFairValue = Math.pow(10, 5.84 * Math.log10(daysSinceGenesis) - 17.01);

  // ── Fear & Greed ──
  let fearGreed = 50;
  let fearGreedLabel = 'Neutral';
  if (fng.status === 'fulfilled') {
    fearGreed = parseInt(fng.value?.data?.[0]?.value ?? '50', 10);
    fearGreedLabel = fng.value?.data?.[0]?.value_classification ?? 'Neutral';
  }

  // ── Market data via Cloudflare Worker proxy ──
  const proxy = proxyQuotes.status === 'fulfilled' ? proxyQuotes.value : {};
  const spx = proxy['^GSPC']?.price ?? null;
  const spxChange = proxy['^GSPC']?.change ?? null;
  const gold = proxy['GC=F']?.price ?? null;
  const goldChange = proxy['GC=F']?.change ?? null;
  const dxy = proxy['DX-Y.NYB']?.price ?? null;
  const dxyChange = proxy['DX-Y.NYB']?.change ?? null;
  const tenYield = proxy['^TNX']?.price ?? null;
  const tenYieldChange = proxy['^TNX']?.change ?? null;
  const etfFlows7d = proxy['MANUAL']?.etfFlows7d ?? null;

  return {
    btcPrice,
    btcChange24h,
    btcVol24h,
    ethPrice,
    ethChange24h,
    spx,
    spxChange,
    gold,
    goldChange,
    dxy,
    dxyChange,
    tenYield,
    tenYieldChange,
    ma50,
    ma200,
    ma200w,
    rsi14,
    fearGreed,
    fearGreedLabel,
    mvrv,                   // approximated: btcPrice / 200D MA
    btcDominance,           // CoinGecko /global — BTC % of total crypto market cap
    daysToHalving,          // computed from next halving block estimate
    fundingRate,            // OKX perpetual swap funding rate
    etfFlows7d,             // manual — updated weekly via dashboard
    powerLawFairValue,      // Santostasi power law model (computed)
    m2Yoy,                  // FRED M2SL YoY % change
  };
}

// ── Hook ─────────────────────────────────────────────────────────────────────
export function useLiveMetrics(): LiveMetrics {
  const cached = loadCache();

  const [metrics, setMetrics] = useState<LiveMetrics>(() => {
    const base: LiveMetrics = {
      btcPrice: 0,
      btcChange24h: 0,
      btcVol24h: 0,
      ethPrice: 0,
      ethChange24h: 0,
      spx: null,
      spxChange: null,
      gold: null,
      goldChange: null,
      dxy: null,
      dxyChange: null,
      tenYield: null,
      tenYieldChange: null,
      ma50: 0,
      ma200: 0,
      ma200w: 0,
      rsi14: 50,
      fearGreed: 50,
      fearGreedLabel: 'Neutral',
      mvrv: null,
      btcDominance: null,
      daysToHalving: null,
      fundingRate: null,
      etfFlows7d: null,
      powerLawFairValue: null,
      m2Yoy: null,
      loading: true,
      error: null,
      lastUpdated: null,
    };
    if (cached) {
      return { ...base, ...cached.data, loading: true, error: null, lastUpdated: new Date(cached.timestamp) };
    }
    return base;
  });

  const refresh = useCallback(async () => {
    try {
      const data = await fetchAllMetrics();
      // Never overwrite good cached values with zeros from a failed API call.
      // If BTC price came back as 0 but we have a cached value, keep the cached value.
      const existing = loadCache();
      const merged = { ...data };
      if (existing && data.btcPrice === 0 && existing.data.btcPrice > 0) {
        // API failed for CoinGecko — keep all CoinGecko-derived fields from cache
        merged.btcPrice = existing.data.btcPrice;
        merged.btcChange24h = existing.data.btcChange24h;
        merged.btcVol24h = existing.data.btcVol24h;
        merged.ethPrice = existing.data.ethPrice;
        merged.ethChange24h = existing.data.ethChange24h;
        merged.ma50 = existing.data.ma50;
        merged.ma200 = existing.data.ma200;
        merged.rsi14 = existing.data.rsi14;
      }
      if (existing && data.fearGreed === 50 && data.fearGreedLabel === 'Neutral' && existing.data.fearGreed !== 50) {
        // Fear & Greed API failed — keep cached
        merged.fearGreed = existing.data.fearGreed;
        merged.fearGreedLabel = existing.data.fearGreedLabel;
      }
      saveCache(merged);
      setMetrics(prev => ({
        ...prev,
        ...merged,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      }));
    } catch (err) {
      // Total failure — keep whatever we had, just mark loading done
      setMetrics(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch metrics',
      }));
    }
  }, []);

  useEffect(() => {
    const cacheEntry = loadCache();
    const isStale = !cacheEntry || Date.now() - cacheEntry.timestamp > CACHE_TTL_MS;

    if (!isStale && cacheEntry) {
      // Cache is fresh — just mark loading done
      setMetrics(prev => ({
        ...prev,
        ...cacheEntry.data,
        loading: false,
        error: null,
        lastUpdated: new Date(cacheEntry.timestamp),
      }));
    } else {
      // Fetch immediately + retry with backoff if critical values missing
      refresh().then(() => {
        // Check if BTC price loaded — if not, retry twice with backoff
        const check = loadCache();
        if (!check || check.data.btcPrice === 0) {
          setTimeout(() => {
            refresh().then(() => {
              const check2 = loadCache();
              if (!check2 || check2.data.btcPrice === 0) {
                setTimeout(refresh, 10000); // 3rd attempt after 10s
              }
            });
          }, 3000); // 2nd attempt after 3s
        }
      });
    }

    // Refresh every 5 minutes
    const interval = setInterval(refresh, CACHE_TTL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  return metrics;
}
